import { useRef, useState, useEffect } from "react";
import { Props_SnippetVideo } from "./Video.props";

const convertIframeUrl = (url: string): string => {
  let newUrl = url;

  // Handle YouTube URLs
  if (url.includes("watch?v=")) {
    newUrl = url.replace("watch?v=", "embed/");
  } else if (url.includes("youtu.be")) {
    newUrl = url.replace("youtu.be/", "youtube.com/embed/");
  }

  // Handle Vimeo URLs
  if (url.includes("vimeo.com")) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) {
      newUrl = `https://player.vimeo.com/video/${match[1]}`;
    }
  }

  // Add autoplay for all URLs if not already present
  if (!newUrl.includes("autoplay=1")) {
    newUrl = `${newUrl}${newUrl.includes("?") ? "&" : "?"}autoplay=1`;
  }

  return newUrl;
};

const Video = (props: Props_SnippetVideo) => {
  const { videoTitle, videoDetails } = props;

  const fullWidth = videoDetails?.videoFullWidth?.value === "true";
  const titleClassname = videoTitle?.showTitle?.value === "true"
    ? "snippet-video__heading"
    : "snippet-video__heading accessible-heading";
  const showModal = videoDetails?.showInModal?.value === "true";
  const convertedUrl = convertIframeUrl(videoDetails?.videoUrl?.value ?? "");
  const [playing, setPlaying] = useState(false);
  const videoSectionClassName =
    "snippet-video__video-section" +
    (fullWidth ? "" : " snippet-video__video-section--not-fullwidth") +
    (playing ? " snippet-video__video-section--playing" : "");
  const additionalId = videoTitle?.snippetid?.value
    ? `-${videoTitle?.snippetid?.value}`
    : "";

  const dialogRef = useRef<HTMLDialogElement>(null);

  const handlePlay = () => {
    setPlaying(true);
    
  };

  const handleClose = () => {
    setPlaying(false);
    if (showModal && dialogRef.current) {
      dialogRef.current.close();
      document.body.classList.remove("modal-open");
    }
  };

  
  useEffect(() => {
    if (playing && showModal && dialogRef.current) {
      dialogRef.current.showModal();
        document.body.classList.add("modal-open");
    }
  }, [playing, showModal]);

  useEffect(() => {
  if (!(playing && showModal)) return;

  const handleEsc = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      handleClose();
    }
  };

  window.addEventListener("keydown", handleEsc);

  return () => {
    window.removeEventListener("keydown", handleEsc);
  };
}, [playing, showModal]);

  return (
    <section className="snippet-video" id={additionalId}>
      <div className={"snippet-video__container"}>
        <h2 className={titleClassname}>{videoTitle.text?.value}</h2>
        <div className={videoSectionClassName}>
          <button
            className="snippet-video__play-button play-button"
            aria-label="play"
            onClick={handlePlay}
          ></button>

          <figure className="snippet-video__image-container">
            <img
              src={videoDetails.image?.value}
              alt={"preview image for " + (videoTitle.text?.value || "")}
              className="snippet-video__image"
              loading="lazy"
              width="1920"
              height="1080"
            />
          </figure>

          {playing && !showModal && (
            <iframe
              width="560"
              height="315"
              src={convertedUrl}
              title="YouTube video player"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              className="snippet-video__video"
              allowFullScreen={true}
            ></iframe>
          )}

          {playing && showModal && (
            <dialog className="snippet-video__video-modal" aria-modal="true" ref={dialogRef}>
              <div className="snippet-video__modal-heading-container">
                <h2 className="snippet-video__modal-heading">
                  {videoTitle.text?.value}
                </h2>
                <button
                  className="snippet-video__close-button close-button"
                  aria-label="close"
                  onClick={handleClose}
                ></button>
              </div>
              <div className="snippet-video__video-container">
                <iframe
                  width="560"
                  height="315"
                  src={convertedUrl}
                  title="YouTube video player"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="snippet-video__video"
                  allowFullScreen={true}
                ></iframe>
              </div>
            </dialog>
          )}
        </div>
      </div>
    </section>
  );
};

export default Video;
